import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { PlaceholderElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/place-holder-component/placeholder-element';
import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import {
    getPlaceHolderType,
    getSize,
} from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { PlaceHolderMode } from '@src/slide/domain/ui-model/element-modeles/property/placeholder/PlaceHolderMode';
import { reverseComponentFlip } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/reverse-component-flip';
import { EventArea } from '@tencent/alloy-editor';
import {
    initPlaceHolderParagraphList,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/place-holder-component/utils';
import { isTitleElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils';
import { isCopyToImageOverSize } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/copy-to-image/over-size-control';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import {
    BaseComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component';
import {
    ComponentTypes,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/component-types';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import {
    ITextBodyStyle,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { TextEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor';
import { WordArtComponent, checkShouldRenderWordArt } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/word-art-component';
import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import { isShowTextFill } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/link';

export interface IPlaceHolderEventHandlers {
    textEditorClickCallback: (e) => void;
    textEditorChangeCallback: (
        textBodyInfo: {
            paragraphList: IParagraph[], textBodyStyle: ITextBodyStyle
        },
        fromEditing?: boolean
    ) => void;
}


export class PlaceHolderComponent extends BaseComponent {
    public clickCallBack: ((type, e) => void) | null;
    private placeHolderGraphics: PlaceholderElement;
    private textEditor: TextEditor;
    private wordArtComponent: WordArtComponent | null;
    private isTemplate: boolean;
    private leftScale: boolean;
    private isPreview: boolean;
    private eventHandlers?: IPlaceHolderEventHandlers;

    public constructor(
        private factoryOptions: InterfaceFactoryOptions<IShapeModel>,
        isTemplate: boolean,
        leftScale: boolean,
        isPreview = false,
    ) {
        super();
        factoryOptions.logicService.setComponentLogic(factoryOptions, this);
        this.isTemplate = isTemplate;
        this.leftScale = leftScale;
        this.isPreview = isPreview;
        this.preRenderWordArt();
        this.renderTextEditor();
        this.renderPlaceHolder();
    }

    public getUniqueId(): string {
        return `${this.factoryOptions.model.key}-${this.factoryOptions.region}-${this.getComponentType()}`;
    }

    public getComponentType(): string {
        return ComponentTypes.PLACEHOLDER;
    }

    public updateComponent = (isPreview = false, leftScale = false): void => {
        this.isPreview = isPreview;
        this.leftScale = leftScale;
        this.renderTextEditor();
        this.renderWordArt();
        this.renderPlaceHolder();
    };

    public registerPlaceHolderClick(callBack: (type, e) => void) {
        this.clickCallBack = callBack;
    }

    public getEditor(): TextEditor {
        return this.textEditor;
    }

    /**
     * 当前 placeholder 是否可编辑文字
     * @returns
     */
    public isTextEditable(): boolean {
        const { model } = this.factoryOptions;
        const type = getPlaceHolderType(model);
        const clearType = [
            PlaceHolderMode.BODY,
            PlaceHolderMode.CTRTITLE,
            PlaceHolderMode.SUBTITLE,
            PlaceHolderMode.TXT,
            PlaceHolderMode.TITLE,
            PlaceHolderMode.SLIDENUM,
            PlaceHolderMode.DATETIME,
            PlaceHolderMode.FOOT,
        ];
        return clearType.includes(type);
    }

    public dispose(): void {
        super.dispose();
        this.clickCallBack = null;
        this.factoryOptions.logicService.removeComponentLogic(this);
    }

    public setEventHandlers(eventHandlers: IPlaceHolderEventHandlers) {
        this.eventHandlers = eventHandlers;
    }

    private renderTextEditor = () => {
        // ssr的情况下不渲染占位符文字
        if (this.isTemplate || this.leftScale || __IS_SSR__) {
            return;
        }
        const { model } = this.factoryOptions;
        const myPHL = initPlaceHolderParagraphList(model);
        const props: IEditorProps = {
            paragraphList: myPHL,
            textBodyStyle: model.textBodyStyle,
            editable: false,
            isTitle: isTitleElement(model),
            onChange: this.eventHandlers?.textEditorChangeCallback,
            shouldUpdate: true,
            textRect: this.getTextRect(),
            eventArea: EventArea.all,
            onClick: this.eventHandlers?.textEditorClickCallback,
            getApplication: this.factoryOptions.getApplication?.bind(this),
            getRenderTextExtraOption: this.getRenderTextExtraOption,
            modelKey: model.key,
            modelKeySuffix: `-${this.factoryOptions.region}-placeHolder`,
            model,
            // 和 office 保持一致，title 和 ctrTitle 不支持设置缩进
            disableIndentation: model.isTitleOrCtrTitlePl,
        };

        this.wordArtComponent?.renderComponentPreprocess({ editorProps: props });

        if (this.textEditor) {
            this.textEditor.updateEditorProps(props);
        } else {
            this.textEditor = new TextEditor(props, { ...this.factoryOptions });
            this.addChild(this.textEditor.editorContainer);
        }
        // 如果图形翻转，则对文本框进行逆向翻转，保证其正常的阅读体验
        reverseComponentFlip(this.textEditor.editorContainer, this.factoryOptions, true);
    };

    private getRenderTextExtraOption = option => ({
        forbidWebgl: FORBID_WEBGL_FILTER,
        effectScale: 1 / CANVAS_PT_SCALE,
        shouldShowTextFill: isShowTextFill(option.textElement),
        textDirection: this.factoryOptions.model
            .textBodyStyle?.wrapperStyle?.textDirection ?? TextDirectionType.HORIZONTAL,
        slideKey: this.factoryOptions.model.slideRef?.key ?? '',
        filterDisplayOptions: this.factoryOptions.getFilterDisplayOptions?.(),
        // 生成图片场景 && 图形大小超过阈值则禁掉滤镜
        forbidFilter: isCopyToImageOverSize(
            this.factoryOptions.exportImageManager?.isImageCopying,
            option?.logicWidth,
            option?.logicHeight,
        ),
    });

    private getTextRect() {
        const { model, frame } = this.factoryOptions;
        const size = getSize(frame, model.props);
        return { left: 0, top: 0, width: size.width, height: size.height };
    }

    private renderPlaceHolder = (): void => {
        // region 为 Template 时不显示页脚边框
        if (this.leftScale || (this.isTemplate && this.isFooterPlaceholder())) {
            return;
        }
        if (!this.placeHolderGraphics) {
            this.placeHolderGraphics = new PlaceholderElement(this.factoryOptions);
            this.addChild(this.placeHolderGraphics);
        } else {
            this.placeHolderGraphics.updatePlaceholder();
        }
    };

    private isFooterPlaceholder() {
        const { model } = this.factoryOptions;
        const type = getPlaceHolderType(model);
        return type === PlaceHolderMode.DATETIME
        || type === PlaceHolderMode.FOOT
        || type === PlaceHolderMode.SLIDENUM;
    }

    // 预渲染艺术字组件
    private preRenderWordArt = () => {
        if (!checkShouldRenderWordArt(this.factoryOptions)) return;
        if (!this.wordArtComponent) {
            this.wordArtComponent = new WordArtComponent(this.factoryOptions, this as any);
        }
    };

    // 渲染艺术字组件
    private renderWordArt = () => {
        this.wordArtComponent?.renderComponent();
    };
}
